import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Users, Layers, Calendar, CheckCircle2 } from "lucide-react";
import { PlanLimitBadge } from "@/components/app/PlanLimitBadge";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { formatBRL } from "@/lib/utils";

export default function BoloesList() {
  const { user } = useAuth();
  const { canAddPool } = usePlanLimits();
  const poolLimit = canAddPool();

  const { data: pools, isLoading } = useQuery({
    queryKey: ["pools", user?.id],
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pools")
        .select("id, name, total_games, total_cost, status, created_at, lottery_id, lotteries(name, slug)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl">Bolões</h1>
          <p className="text-sm text-muted-foreground">Monte fechamentos com cobertura máxima e divida cotas</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/app/boloes/conferencias">
              <CheckCircle2 className="mr-2 h-4 w-4" /> Conferências
            </Link>
          </Button>
          {poolLimit.ok ? (
            <Button asChild>
              <Link to="/app/boloes/novo">
                <Plus className="mr-2 h-4 w-4" /> Novo bolão
              </Link>
            </Button>
          ) : (
            <Button asChild variant="outline">
              <Link to="/planos">
                <Plus className="mr-2 h-4 w-4" /> Limite atingido — fazer upgrade
              </Link>
            </Button>
          )}
        </div>
      </div>

      <PlanLimitBadge resource="active_pools" label="Bolões ativos" />

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
      )}

      {!isLoading && (!pools || pools.length === 0) && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Layers className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="font-serif text-xl">Nenhum bolão ainda</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Crie um bolão escolhendo dezenas, defina cotas entre participantes e gere todas as combinações automaticamente.
            </p>
            <Button asChild className="mt-6">
              <Link to="/app/boloes/novo">
                <Plus className="mr-2 h-4 w-4" /> Criar primeiro bolão
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && pools && pools.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pools.map((p: any) => (
            <Link key={p.id} to={`/app/boloes/${p.id}`}>
              <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
                <CardContent className="space-y-3 pt-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-serif text-lg">{p.name}</div>
                    <span className="rounded-full border border-lt-teal/40 bg-lt-teal/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-lt-teal">
                      {p.status}
                    </span>
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{p.lotteries?.name}</div>
                  <div className="grid grid-cols-2 gap-3 text-[13px]">
                    <div className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-muted-foreground" />{p.total_games} jogos</div>
                    <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-muted-foreground" />{formatBRL(Number(p.total_cost))}</div>
                    <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground"><Calendar className="h-3 w-3" />{new Date(p.created_at).toLocaleDateString("pt-BR")}</div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
